import api from '@/services/Api';
import { RecyclingFolder } from '@/models/recyclingModule/recyclingFolder/RecyclingFolder'
import { RecyclingFolderBuybackDetail } from '@/models/recyclingModule/recyclingFolder/RecyclingFolderBuybackDetail'
import { RecyclingFolderProductDetail } from '@/models/recyclingModule/recyclingFolder/RecyclingFolderProductDetail'
import { RecyclingFolderAttachment } from '@/models/recyclingModule/recyclingFolder/RecyclingFolderAttachment'
import { RecyclingFolderCount } from '@/models/recyclingModule/recyclingFolder/RecyclingFolderCount'
import { RecyclingFolderPriceReport } from '@/models/recyclingModule/recyclingFolder/RecyclingFolderPriceReport'
import { ImpactYearly } from '@/models/recyclingModule/report/ImpactYearly'
import { ImpactReport } from '@/models/recyclingModule/report/ImpactReport'
import { ImpactCount } from '@/models/recyclingModule/report/ImpactCount'
import { ImpactAllIndex } from '@/models/recyclingModule/report/ImpactAllIndex'

class RecyclingFolderService {
  async getRecyclingFolder(recyclingFolderBoomerangId) {
    const response = await api.get(
      '/recycling/distributor/recycling-folder/' + recyclingFolderBoomerangId)

    const productDetail = new RecyclingFolderProductDetail(
      response.data.productDetail.technicalId,
      response.data.productDetail.boomerangId,
      response.data.productDetail.boomerangName,
      response.data.productDetail.manufacturerName,
      response.data.productDetail.description,
      response.data.productDetail.picture,
      response.data.productDetail.type,
      response.data.productDetail.createdAt
    )

    const buybackDetail = new RecyclingFolderBuybackDetail(
      response.data.buybackDetail.technicalId,
      response.data.buybackDetail.recyclingBoomerangId,
      response.data.buybackDetail.recyclingName,
      response.data.buybackDetail.recyclingBuybackPriceId,
      response.data.buybackDetail.buybackPrice,
      response.data.buybackDetail.buybackClientPart,
      response.data.buybackDetail.buybackStorePart,
      response.data.buybackDetail.conditionTitle,
      response.data.buybackDetail.conditionDetail,
      response.data.buybackDetail.isRecycler,
      response.data.buybackDetail.createdAt
    )

    const attachmentList = [];
    for (let attachment of response.data.attachmentList) {
      attachmentList.push(new RecyclingFolderAttachment(
        attachment.technicalId,
        attachment.recyclingFolderId,
        attachment.authorId,
        attachment.authorFirstname,
        attachment.authorLastname,
        attachment.attachmentName,
        attachment.filePath,
        attachment.attachmentCounter,
        attachment.createdAt
      ))
    }

    return new RecyclingFolder(
      response.data.technicalId,
      response.data.boomerangId,
      response.data.clientCompanyName,
      response.data.status,
      response.data.depositDate,
      response.data.manufacturerBoomerangId,
      response.data.productBoomerangId,
      productDetail,
      response.data.productSerialNumber,
      response.data.repairFileBoomerangId,
      buybackDetail,
      response.data.networkBoomerangId,
      response.data.storeBoomerangId,
      attachmentList,
      response.data.hasDefaultProduct,
      response.data.createdAt,
      response.data.createdBy
    )
  }

  async createRecyclingFolder(data) {
    const response = await api.post(
      '/recycling/distributor/recycling-folder/with-product',
      data
    )

    return response.data
  }

  async createRecyclingFolderWithoutProduct(data) {
    const response = await api.post(
      '/recycling/distributor/recycling-folder/without-product',
      data
    )

    return response.data
  }

  async updateRecyclingFolderProductChecking(recyclingFolderBoomerangId, data) {
    const response = await api.put(
      '/recycling/distributor/recycling-folder/product-checking/' + recyclingFolderBoomerangId,
      data
    )

    return response.data
  }

  async updateRecyclingFolderProductPackaging(recyclingFolderBoomerangId, data) {
    const response = await api.put(
      '/recycling/distributor/recycling-folder/product-packaging/' + recyclingFolderBoomerangId,
      data
    )

    return response.data
  }

  async updateRecyclingFolderProductInformation(recyclingFolderBoomerangId, data) {
    const response = await api.put(
      '/recycling/distributor/recycling-folder/product-information/with-product/' + recyclingFolderBoomerangId,
      data
    )

    return response.data
  }

  async updateRecyclingFolderProductInformationWithoutProduct(recyclingFolderBoomerangId, data) {
    const response = await api.put(
      '/recycling/distributor/recycling-folder/product-information/without-product/' + recyclingFolderBoomerangId,
      data
    )

    return response.data
  }

  async addNewAttachmentToRecyclingFolder(formData) {
    const response = await api.put(
      '/recycling/distributor/recycling-folder/add-attachment',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

    return response.data
  }

  async removeAttachmentFromRecyclingFolder(data) {
    const response = await api.put(
      '/recycling/distributor/recycling-folder/remove-attachment',
      data)
    return response.data
  }

  async getQRCodeToken(recyclingFolderBoomerangId, requesterCollaboratorId) {
    const response = await api.get(
      '/recycling/distributor/recycling-folder/qr-code-token/' + recyclingFolderBoomerangId
      + '?requester_collaborator_id=' + requesterCollaboratorId)
    return response.data
  }

  async confirmAttachmentAddedByQrCode(recyclingFolderBoomerangId,
                                       qrCodeToken,
                                       requesterCollaboratorId) {
    let data = {
      recyclingFolderBoomerangId: recyclingFolderBoomerangId,
      qrCodeToken: qrCodeToken,
      requesterCollaboratorId: requesterCollaboratorId
    }

    const response = await api.put(
      '/recycling/distributor/recycling-folder/confirm-attachment-added-by-qr-code',
      data
    )
    return response.data
  }

  async validateRecyclingFolderQrCodeToken(token){
    const data = {
      qrCodeToken: token
    }
    const response = await api.post(
      '/recycling/distributor/recycling-folder/qr-code-token-validation',
      data)

    return response.data
  }

  async sendAttachmentWithQrCodeToken(data) {
    const response = await api.put(
      '/recycling/distributor/recycling-folder/send-attachment-with-qr-code',
      data,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
    return response.data
  }

  async getCertificate(recyclingFolderBoomerangId) {
    const response = await api.get(
      '/recycling/distributor/recycling-folder/certificate/' + recyclingFolderBoomerangId,
      { responseType: 'blob' })
    return response.data
  }

  async getRecyclingFolderCountByStore(storeBoomerangId) {
    const response = await api.get(
      '/recycling/distributor/recycling-folder/count-by-store/' + storeBoomerangId)

    return new RecyclingFolderCount(
      response.data.storeBoomerangId,
      response.data.count
    )
  }

  async getRecyclingFolderPriceReportByStore(storeBoomerangId) {
    const response = await api.get(
      '/recycling/distributor/recycling-folder/price-report-by-store/' + storeBoomerangId)

    return new RecyclingFolderPriceReport(
      response.data.storeBoomerangId,
      response.data.totalPrice
    )
  }

  async collectionTrigger(storeBoomerangId, bacBoomerangId) {
    const response = await api.put(
      '/recycling/distributor/recycling-folder/collection-trigger/' + storeBoomerangId + '/' + bacBoomerangId)
    return response.data
  }

  async getRecyclingFolderReportByStore(storeBoomerangId) {
    const response = await api.get(
      '/recycling/distributor/recycling-folder/export/' + storeBoomerangId,
      { responseType: 'blob' })
    return response.data
  }

  async getRecyclingFolderFullReport(storeIdList, manufacturerIdList) {
    const searchParams = new URLSearchParams()

    storeIdList.forEach(id => searchParams.append('storeIdList', id))
    manufacturerIdList.forEach(id => searchParams.append('manufacturerIdList', id))

    const response = await api.get(
      '/recycling/distributor/recycling-folder/report',
      { params: searchParams }
    )

    return new ImpactYearly(
      new ImpactReport(
        response.data.currentYearCount.recycledByBoomerangCount,
        response.data.currentYearCount.recycledByRepairerCount,
        new ImpactCount(
          response.data.currentYearCount.repaired.ecosystemBoomerangCount,
          response.data.currentYearCount.repaired.ecosystemImpactMaterialCount,
          response.data.currentYearCount.repaired.ecosystemImpactCo2Count,
          response.data.currentYearCount.repaired.ademeOtherCount,
          response.data.currentYearCount.repaired.ademeImpactMaterialCount,
          response.data.currentYearCount.repaired.ademeImpactCo2Count,
          response.data.currentYearCount.repaired.withoutEvaluationCount,
        ),
        new ImpactCount(
          response.data.currentYearCount.other.ecosystemBoomerangCount,
          response.data.currentYearCount.other.ecosystemImpactMaterialCount,
          response.data.currentYearCount.other.ecosystemImpactCo2Count,
          response.data.currentYearCount.other.ademeOtherCount,
          response.data.currentYearCount.other.ademeImpactMaterialCount,
          response.data.currentYearCount.other.ademeImpactCo2Count,
          response.data.currentYearCount.other.withoutEvaluationCount,
        ),
      ),
      new ImpactReport(
        response.data.lastYearCount.recycledByBoomerangCount,
        response.data.lastYearCount.recycledByRepairerCount,
        new ImpactCount(
          response.data.lastYearCount.repaired.ecosystemBoomerangCount,
          response.data.lastYearCount.repaired.ecosystemImpactMaterialCount,
          response.data.lastYearCount.repaired.ecosystemImpactCo2Count,
          response.data.lastYearCount.repaired.ademeOtherCount,
          response.data.lastYearCount.repaired.ademeImpactMaterialCount,
          response.data.lastYearCount.repaired.ademeImpactCo2Count,
          response.data.lastYearCount.repaired.withoutEvaluationCount,
        ),
        new ImpactCount(
          response.data.lastYearCount.other.ecosystemBoomerangCount,
          response.data.lastYearCount.other.ecosystemImpactMaterialCount,
          response.data.lastYearCount.other.ecosystemImpactCo2Count,
          response.data.lastYearCount.other.ademeOtherCount,
          response.data.lastYearCount.other.ademeImpactMaterialCount,
          response.data.lastYearCount.other.ademeImpactCo2Count,
          response.data.lastYearCount.other.withoutEvaluationCount,
        ),
      ),
      new ImpactReport(
        response.data.twoYearsAgoCount.recycledByBoomerangCount,
        response.data.twoYearsAgoCount.recycledByRepairerCount,
        new ImpactCount(
          response.data.twoYearsAgoCount.repaired.ecosystemBoomerangCount,
          response.data.twoYearsAgoCount.repaired.ecosystemImpactMaterialCount,
          response.data.twoYearsAgoCount.repaired.ecosystemImpactCo2Count,
          response.data.twoYearsAgoCount.repaired.ademeOtherCount,
          response.data.twoYearsAgoCount.repaired.ademeImpactMaterialCount,
          response.data.twoYearsAgoCount.repaired.ademeImpactCo2Count,
          response.data.twoYearsAgoCount.repaired.withoutEvaluationCount,
        ),
        new ImpactCount(
          response.data.twoYearsAgoCount.other.ecosystemBoomerangCount,
          response.data.twoYearsAgoCount.other.ecosystemImpactMaterialCount,
          response.data.twoYearsAgoCount.other.ecosystemImpactCo2Count,
          response.data.twoYearsAgoCount.other.ademeOtherCount,
          response.data.twoYearsAgoCount.other.ademeImpactMaterialCount,
          response.data.twoYearsAgoCount.other.ademeImpactCo2Count,
          response.data.twoYearsAgoCount.other.withoutEvaluationCount,
        ),
      )
    )
  }

  async getRecyclingFolderReportAllIndex(storeIdList, manufacturerIdList) {
    const searchParams = new URLSearchParams()

    storeIdList.forEach(id => searchParams.append('storeIdList', id))
    manufacturerIdList.forEach(id => searchParams.append('manufacturerIdList', id))

    const response = await api.get(
      '/recycling/distributor/recycling-folder/report-all-index',
      { params: searchParams }
    )

    return new ImpactAllIndex(
      response.data.impactMaterialCount,
      response.data.impactCo2Count
    )
  }
}

export default new RecyclingFolderService()
